import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Folder as ProjectIcon,
  Star as TargetIcon,
  CalendarMonth as CalendarIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import ProjectGroupList from '../../components/files/ProjectGroupList';
import TargetGroupList from '../../components/files/TargetGroupList';
import DateGroupList from '../../components/files/DateGroupList';
import DownloadProgressDialog from '../../components/files/DownloadProgressDialog';
import ConfirmDialog from '../../components/ConfirmDialog';
import PushExternallyDialog from '../../components/PushExternallyDialog';
import { useFileDownload } from '../../hooks/useFileDownload';
import { useAuth } from '../../contexts/AuthContext';
import { formatSize } from '../../utils/fileUtils';
import { apiService } from '../../services/apiService';
import { getAccessToken } from '../../services/authStorage';

const TAB_KEY = 'fileBrowser_tab';
const TAB_LABELS = ['projects', 'targets', 'dates'];

const API_BASE_URL = '/api/v1';

const FileBrowser = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'server_admin' || user?.role === 'admin' || user?.role === 'instructor';
  const [tab, setTab] = useState(() => {
    const saved = sessionStorage.getItem(TAB_KEY);
    return saved !== null ? parseInt(saved, 10) : 1; // Default to "By Target"
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [previewDialog, setPreviewDialog] = useState({ open: false, url: null, name: null });
  const [stats, setStats] = useState(null);
  const [calibrateError, setCalibrateError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [pushDialog, setPushDialog] = useState({ open: false, observationId: null, projectId: null, projectName: null, target: null, date: null });
  const { downloadGroup, downloadProgress, cancelDownload, getDownloadUrl, getCalibratedDownloadUrl, downloadCalibrated } = useFileDownload();

  const handleTabChange = (_, newValue) => {
    setTab(newValue);
    setStats(null);
    sessionStorage.setItem(TAB_KEY, newValue);
  };

  const handleDownloadGroup = (groupType, groupId, groupName) => {
    downloadGroup(groupType, groupId);
  };

  // Push Externally, mirroring the download action, for any grouping.
  const handlePushGroup = (groupType, groupId, groupName) => {
    const base = { open: true, observationId: null, projectId: null, projectName: null, target: null, date: null };
    if (groupType === 'project') {
      setPushDialog({ ...base, projectId: groupId, projectName: groupName });
    } else if (groupType === 'observation') {
      setPushDialog({ ...base, observationId: groupId });
    } else if (groupType === 'target') {
      setPushDialog({ ...base, target: groupId });
    } else if (groupType === 'date') {
      setPushDialog({ ...base, date: groupId });
    }
  };

  const handlePreview = (url, name) => {
    setPreviewDialog({ open: true, url, name });
  };

  const handleStatsLoaded = useCallback((newStats) => {
    setStats(newStats);
  }, []);

  const handleDeleteFile = useCallback((fileId, filename) => {
    setConfirm({
      title: 'Delete File',
      message: <span>Are you sure you want to delete <strong>{filename}</strong>?</span>,
      confirmLabel: 'Delete',
      confirmColor: 'error',
      confirmIcon: <DeleteIcon />,
      icon: <DeleteIcon color="error" />,
      onConfirm: async () => {
        try {
          const token = getAccessToken();
          await axios.delete(`${API_BASE_URL}/user-files/${fileId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setRefreshKey(k => k + 1);
        } catch (err) {
          setCalibrateError('Failed to delete file');
          throw err;
        }
      }
    });
  }, []);

  const handleBulkDeleteFiles = useCallback((fileIds) => {
    if (!fileIds || fileIds.length === 0) return;
    const count = fileIds.length;
    setConfirm({
      title: 'Delete Files',
      message: <span>Are you sure you want to delete <strong>{count}</strong> file{count === 1 ? '' : 's'}? This cannot be undone.</span>,
      confirmLabel: 'Delete',
      confirmColor: 'error',
      confirmIcon: <DeleteIcon />,
      icon: <DeleteIcon color="error" />,
      onConfirm: async () => {
        try {
          const result = await apiService.bulkDeleteFiles(fileIds);
          const failed = result?.failed || result?.data?.failed || 0;
          if (failed > 0) {
            const errs = result?.errors || result?.data?.errors || [];
            setCalibrateError(`Deleted ${result?.deleted ?? result?.data?.deleted ?? '?'} file(s), ${failed} failed${errs.length ? `: ${errs.map(e => e.error || e).join(', ')}` : ''}`);
          }
          setRefreshKey(k => k + 1);
        } catch (err) {
          setCalibrateError('Failed to delete selected files');
          throw err;
        }
      }
    });
  }, []);

  const canDeleteFile = useCallback((file) => {
    if (isAdmin) return true;
    // Compare string forms. The auth user object uses `id` (not `_id`), and the file's
    // user_id may be a populated object or an ObjectId/string.
    const fileUid = (file.user_id?._id || file.user_id)?.toString();
    const myId = (user?._id || user?.id)?.toString();
    return !!fileUid && !!myId && fileUid === myId;
  }, [isAdmin, user]);

  return (
    <Box sx={{ p: 3 }}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h4">
            My Files
          </Typography>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { setRefreshKey(k => k + 1); setStats(null); }}>
            Refresh
          </Button>
        </Box>

        {/* Tab Selector and Search */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tabs value={tab} onChange={handleTabChange}>
              <Tab icon={<ProjectIcon />} iconPosition="start" label="By Project" />
              <Tab icon={<TargetIcon />} iconPosition="start" label="By Target" />
              <Tab icon={<CalendarIcon />} iconPosition="start" label="By Date" />
            </Tabs>

            <TextField
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
              }}
            />
          </Box>
        </Paper>

        {/* Summary Stats */}
        {stats && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {stats.groups} {TAB_LABELS[tab]} &middot; {stats.observations} observations &middot; {stats.files} files &middot; {formatSize(stats.bytes)}
          </Typography>
        )}

        {/* Tab Content */}
        {tab === 0 && (
          <ProjectGroupList
            key={refreshKey}
            searchTerm={searchTerm}
            onDownloadGroup={handleDownloadGroup}
            onPushGroup={handlePushGroup}
            onPreview={handlePreview}
            getDownloadUrl={getDownloadUrl}
            getCalibratedDownloadUrl={getCalibratedDownloadUrl}
            downloadCalibrated={downloadCalibrated}
            onCalibrateError={setCalibrateError}
            onStatsLoaded={handleStatsLoaded}
            onDelete={handleDeleteFile}
            canDelete={canDeleteFile}
            onBulkDelete={handleBulkDeleteFiles}
          />
        )}
        {tab === 1 && (
          <TargetGroupList
            key={refreshKey}
            searchTerm={searchTerm}
            onDownloadGroup={handleDownloadGroup}
            onPushGroup={handlePushGroup}
            onPreview={handlePreview}
            getDownloadUrl={getDownloadUrl}
            getCalibratedDownloadUrl={getCalibratedDownloadUrl}
            downloadCalibrated={downloadCalibrated}
            onCalibrateError={setCalibrateError}
            onStatsLoaded={handleStatsLoaded}
            onDelete={handleDeleteFile}
            canDelete={canDeleteFile}
            onBulkDelete={handleBulkDeleteFiles}
          />
        )}
        {tab === 2 && (
          <DateGroupList
            key={refreshKey}
            searchTerm={searchTerm}
            onDownloadGroup={handleDownloadGroup}
            onPushGroup={handlePushGroup}
            onPreview={handlePreview}
            getDownloadUrl={getDownloadUrl}
            getCalibratedDownloadUrl={getCalibratedDownloadUrl}
            downloadCalibrated={downloadCalibrated}
            onCalibrateError={setCalibrateError}
            onStatsLoaded={handleStatsLoaded}
            onDelete={handleDeleteFile}
            canDelete={canDeleteFile}
            onBulkDelete={handleBulkDeleteFiles}
          />
        )}
      </Box>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, url: null, name: null })}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {previewDialog.name || 'Image Preview'}
          </Typography>
          <IconButton onClick={() => setPreviewDialog({ open: false, url: null, name: null })} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{
          p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center',
          bgcolor: '#0a0a0a', minHeight: '400px', maxHeight: '85vh', overflow: 'auto'
        }}>
          {previewDialog.url && (
            <img
              src={previewDialog.url}
              alt={previewDialog.name}
              style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div style="padding: 40px; color: #999;">Failed to load image</div>';
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Download Progress */}
      <DownloadProgressDialog progress={downloadProgress} onClose={cancelDownload} />

      {/* Calibration Error Snackbar */}
      <Snackbar
        open={Boolean(calibrateError)}
        autoHideDuration={8000}
        onClose={() => setCalibrateError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCalibrateError(null)} severity="warning" variant="filled" sx={{ width: '100%' }}>
          {calibrateError}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        confirmIcon={confirm?.confirmIcon}
        icon={confirm?.icon}
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />

      <PushExternallyDialog
        open={pushDialog.open}
        onClose={() => setPushDialog((p) => ({ ...p, open: false }))}
        observationId={pushDialog.observationId}
        projectId={pushDialog.projectId}
        projectName={pushDialog.projectName}
        target={pushDialog.target}
        date={pushDialog.date}
      />
    </Box>
  );
};

export default FileBrowser;
